import React, { useCallback, useState } from "react";
import { DateInput, IconChevronLeft, IconChevronRight, TextIconButton } from "@geotab/zenith";

import { getNextDayStart, getStartOfDayUserTimezone } from "../../../utils/date";
import { getServerDate } from "../../../utils/storage/date";
interface IDateSelector {
    callback: (date: Date) => void;
    minDate?: Date;
    maxDate?: Date;
    initialDate?: Date;
}

/**
 * DateSelector component used the @geotab/zenith component to make a component similar to date range
 * with additional customization options for next and prev date buttons.
 *
 * @example
 * ```tsx
 * <DateSelector callback={(date) => handleDateChange(date)} />
 * ```
 */
export const DateSelector: React.FC<IDateSelector> = ({ callback = () => {}, minDate, maxDate, initialDate }) => {
    const [dateValue, setDateValue] = useState<Date>(initialDate || getStartOfDayUserTimezone(getServerDate()));

    const handleChange = useCallback((newValue: string | undefined) => {
        if (!newValue) {
            return;
        }

        const newDate = getStartOfDayUserTimezone(new Date(newValue));
        callback(newDate);
        setDateValue(newDate);
    }, [callback]);

    const changeDateBy = (days: number) => {
        let currentDate = new Date(dateValue);
        currentDate.setDate(currentDate.getDate() + days);

        handleChange(currentDate?.toISOString());
    };

    const canGoPrev = !minDate || dateValue > minDate;
    const canGoNext = !maxDate || dateValue < maxDate;
    // Zenith treats disableDatesAfter as exclusive, so offset by +1 day
    const disableDatesAfterValue = maxDate ? getNextDayStart(maxDate, true).toDateString() : undefined;

    const setPrevDate = () => {
        if (canGoPrev) {
            changeDateBy(-1);
        }
    };

    const setNextDate = () => {
        if (canGoNext) {
            changeDateBy(1);
        }
    };

    return (
        <div className="zenith-date-selector-container">
            <TextIconButton icon={IconChevronLeft} onClick={setPrevDate} id="previousButton" disabled={!canGoPrev} aria-label="Previous day" />
            <DateInput
                className="date-selector"
                value={dateValue?.toISOString()}
                onChange={handleChange}
                disableDatesBefore={minDate?.toDateString()}
                disableDatesAfter={disableDatesAfterValue}
            />
            <TextIconButton icon={IconChevronRight} onClick={setNextDate} id="nextButton" disabled={!canGoNext} aria-label="Next day" />
        </div>
    );
};

export default DateSelector;
